import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import {
  FiArrowRight,
  FiCalendar,
  FiMapPin,
  FiMic,
  FiMusic,
  FiStar,
} from "react-icons/fi";
import AnimatedContainer from "../../container/animatedContainer";
import { useGetEventsQuery } from "../../api/event";
import { campaign, isCampaignEvent } from "../../config/campaign";
import { isEventSalesClosed, sortEventPricings } from "../../utils";

export function HomePage() {
  const { data, isLoading } = useGetEventsQuery({
    pageSize: campaign.stops.length,
    search: campaign.title,
  });
  const stopEventsBySlug = new Map(
    data?.list.map((item) => [item.slug, item]) ?? []
  );
  const nextOpenStop =
    campaign.stops.find((stop) => {
      const stopEvent = stopEventsBySlug.get(stop.slug);
      return !isCampaignStopClosed(stop.isoDate, stopEvent);
    }) ?? campaign.stops[0];
  const campaignEvent =
    stopEventsBySlug.get(nextOpenStop.slug) ??
    data?.list.find((item) => isCampaignEvent(item.title, item.slug));
  const eventLink = `/event/${campaignEvent?.slug ?? nextOpenStop.slug}`;
  const ticketTiers =
    campaignEvent?.pricings
      ? sortEventPricings(campaignEvent.pricings)
      .map((item) => ({
        type: item.type,
        price: item.price,
      }))
      : campaign.fallbackTickets;

  return (
    <AnimatedContainer>
      <div className="min-h-screen bg-[#120202] text-white">
        <section className="relative min-h-[74svh] overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url('${campaign.poster}')` }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#120202] via-[#170303]/85 to-[#5c0706]/35" />
          <div className="absolute inset-x-0 bottom-0 h-36 bg-gradient-to-t from-[#120202] to-transparent" />

          <div className="relative mx-auto flex min-h-[74svh] w-full max-w-[1500px] flex-col justify-end px-5 pb-12 pt-16 md:px-12 lg:px-16">
            <div className="max-w-3xl">
              <p className="text-sm font-bold uppercase tracking-[0.28em] text-[#ffb45f]">
                {campaign.organiser} presents
              </p>
              <h1 className="mt-4 max-w-2xl text-5xl font-black uppercase leading-[0.92] text-[#ffe3a4] sm:text-7xl lg:text-8xl">
                {campaign.title}
              </h1>
              <p className="mt-4 text-3xl font-black text-white sm:text-5xl">
                {campaign.year}
              </p>
              <p className="mt-5 max-w-2xl text-base font-medium leading-7 text-white/80 sm:text-lg">
                Bruce Melodie, The Ben Tiger B, Kitoko and Bwiza on one
                country-wide stage, powered by {campaign.poweredBy}.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  to={eventLink}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-[#ff5a1f] px-6 py-4 text-sm font-black uppercase tracking-[0.12em] text-white shadow-[0_18px_45px_rgba(255,90,31,0.32)] transition hover:bg-[#ff7138]"
                >
                  Buy tickets
                  <FiArrowRight className="text-lg" />
                </Link>
                <a
                  href="#tour-stops"
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-white/25 bg-white/10 px-6 py-4 text-sm font-black uppercase tracking-[0.12em] text-white transition hover:bg-white/15"
                >
                  View stops
                  <FiMapPin className="text-lg" />
                </a>
              </div>
            </div>

            <div className="mt-10 grid w-full max-w-4xl grid-cols-1 gap-3 sm:grid-cols-3">
              <HeroStat
                icon={<FiMic />}
                label="Artists"
                value={campaign.artists.length.toString()}
              />
              <HeroStat
                icon={<FiMapPin />}
                label="Selected stops"
                value={campaign.stops.length.toString()}
              />
              <HeroStat icon={<FiStar />} label="Ticket tiers" value="3" />
            </div>
          </div>
        </section>

        <section
          id="tour-stops"
          className="mx-auto grid w-full max-w-[1500px] grid-cols-1 gap-5 px-5 py-10 md:px-12 lg:grid-cols-[0.95fr_1.05fr] lg:px-16"
        >
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-[#ffb45f]">
              Four selected events
            </p>
            <h2 className="mt-2 max-w-2xl text-3xl font-black uppercase text-white sm:text-5xl">
              Pick your Summer Country Tour stop.
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {campaign.stops.map((stop, index) => {
              const stopEvent = stopEventsBySlug.get(stop.slug);
              const isStopClosed = isCampaignStopClosed(
                stop.isoDate,
                stopEvent
              );

              return (
                <Link
                  to={`/event/${stop.slug}`}
                  key={stop.id}
                  className={`group rounded-lg border border-white/10 bg-white/[0.06] p-4 transition hover:-translate-y-1 hover:border-[#ffb45f]/60 hover:bg-white/[0.09] ${
                    isStopClosed ? "opacity-65" : ""
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#ff5a1f] text-xl text-white">
                      <FiMapPin />
                    </div>
                    <span className="text-xs font-black uppercase tracking-[0.2em] text-white/35">
                      {isStopClosed ? "Done" : `0${index + 1}`}
                    </span>
                  </div>
                  <h3 className="mt-5 text-2xl font-black text-[#ffe3a4]">
                    {stop.city}
                  </h3>
                  <p className="mt-2 flex items-center gap-2 text-sm font-bold uppercase tracking-[0.12em] text-white/70">
                    <FiCalendar />
                    {stop.date}
                  </p>
                  <div className="mt-5 inline-flex items-center gap-2 text-sm font-black uppercase tracking-[0.12em] text-[#ffb45f]">
                    {isStopClosed ? "View stop" : "Select stop"}
                    <FiArrowRight className="transition group-hover:translate-x-1" />
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        <section className="mx-auto grid w-full max-w-[1500px] grid-cols-1 gap-5 px-5 pb-14 md:px-12 lg:grid-cols-[1.1fr_0.9fr] lg:px-16">
          <div className="rounded-lg border border-white/10 bg-[#1d0505] p-5 md:p-7">
            <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.22em] text-[#ffb45f]">
                  Entrance fees
                </p>
                <h2 className="mt-2 text-3xl font-black uppercase text-white sm:text-4xl">
                  Choose your ticket tier.
                </h2>
              </div>
              <Link
                to={eventLink}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-white px-5 py-3 text-sm font-black uppercase tracking-[0.12em] text-[#120202] transition hover:bg-[#ffe3a4]"
              >
                Checkout
                <FiArrowRight />
              </Link>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-3">
              {isLoading
                ? [1, 2, 3].map((item) => (
                    <div
                      key={item}
                      className="h-36 rounded-lg bg-white/10 animate-pulse"
                    />
                  ))
                : ticketTiers.map((tier) => (
                    <Link
                      key={tier.type}
                      to={eventLink}
                      className="rounded-lg border border-white/10 bg-white/[0.06] p-5 transition hover:border-[#ffb45f]/70 hover:bg-white/[0.1]"
                    >
                      <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#ffb45f] text-xl text-[#120202]">
                        <FiMusic />
                      </div>
                      <p className="mt-5 text-sm font-black uppercase tracking-[0.18em] text-white/55">
                        {tier.type}
                      </p>
                      <p className="mt-2 text-3xl font-black text-white">
                        {tier.price.toLocaleString()} RWF
                      </p>
                    </Link>
                  ))}
            </div>
          </div>

          <div className="min-h-72 overflow-hidden rounded-lg border border-white/10 bg-white/[0.06]">
            <div
              className="h-full min-h-72 bg-cover bg-center"
              style={{ backgroundImage: `url('${campaign.poster}')` }}
              aria-label={`${campaign.title} poster`}
            />
          </div>
        </section>
      </div>
    </AnimatedContainer>
  );
}

function isCampaignStopClosed(
  isoDate: string,
  event?: { endDate?: string; date?: string; status?: string }
) {
  return (
    isEventSalesClosed(event) ||
    isEventSalesClosed({ endDate: isoDate, status: "active" })
  );
}

function HeroStat({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.08] p-4 backdrop-blur">
      <div className="flex h-10 w-10 items-center justify-center rounded-md bg-white/10 text-xl text-[#ffb45f]">
        {icon}
      </div>
      <div>
        <p className="text-2xl font-black text-white">{value}</p>
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/50">
          {label}
        </p>
      </div>
    </div>
  );
}
